import {
  fetchSearchRepositories,
  fetchTopRepositories,
  type RepositoryPickerRepository,
  type RepositoryPickerRepositoryLight,
} from '@github-ui/item-picker/RepositoryPicker'
import {getRepositorySearchQuery} from '@github-ui/item-picker/utils/get-repository-search-query'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import type {Subscription} from 'relay-runtime'

import type {TopicItem} from '../utils/copilot-chat-types'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {getRepositoryFromOrgSearchQuery} from '../utils/custom-copilots-helpers'

export function useRepositoryItems(
  filterText: string,
  open: boolean,
  topicDisplayCount?: number,
  ownerDisplayLogin?: string,
  includeForks: boolean = false,
) {
  const [topRepositoryResults, setTopRepositoryResults] = useState<RepositoryPickerRepository[] | undefined>()
  const resetTopRepoResults = useCallback(() => setTopRepositoryResults(undefined), [])

  const {topRepositoriesCache, currentRepository} = useChatState()

  const chatManager = useChatManager()
  const [repositories, setRepositories] = useState<TopicItem[]>([])
  const [loading, setLoading] = useState<true | false | 'initial'>(!topRepositoriesCache ? 'initial' : false)

  const searchedReposSubscription = useRef<Subscription | undefined>(undefined)
  const relayEnvironment = useRelayEnvironment()

  // Currently only used by Spaces to filter repositories by the owner for org-owned spaces.
  const isOrgOwned = ownerDisplayLogin && ownerDisplayLogin.trim() !== ''

  if (topRepositoryResults && !topRepositoriesCache && !filterText && !isOrgOwned) {
    const topReposMapped = getListItemsFromRepos(topRepositoryResults || [])

    const {ownerLogin, name} = currentRepository ?? {}
    if (
      currentRepository &&
      !topReposMapped.find(repo => `${repo.ownerLogin}/${repo.name}` === `${ownerLogin}/${name}`)
    ) {
      topReposMapped.splice(0, 0, {
        databaseId: currentRepository.id,
        isInOrganization: currentRepository.ownerType === 'Organization',
        name: currentRepository.name,
        nwo: `${currentRepository.ownerLogin}/${currentRepository.name}`,
        ownerLogin: currentRepository.ownerLogin,
        ownerAvatarUrl: `/${currentRepository.ownerLogin}.png?s=40`,
      })
    }
    // setTimeout to prevent a race condition and a warning from React
    // Update TopicList first with the top repositories and then update CopilotChatProvider
    setTimeout(() => chatManager.setTopRepositoryTopics(topReposMapped), 10)
    setLoading(false)
    // We already set the cache so reset the top repos so we stop calling Relay
    setTopRepositoryResults(undefined)
  }

  const fetchTopRepos = useCallback(async () => {
    try {
      const topRepos = await fetchTopRepositories(relayEnvironment, {
        first: topicDisplayCount,
        owner: isOrgOwned ? ownerDisplayLogin : undefined,
      })
      setTopRepositoryResults(topRepos)
    } catch {
      // If we have an error, just silently catch
      // We can't return because we want to use the useFragment hook
      // But we can check for null later
    } finally {
      setLoading(false)
    }
  }, [isOrgOwned, ownerDisplayLogin, relayEnvironment, setTopRepositoryResults, topicDisplayCount])

  useEffect(() => {
    if (open && !isOrgOwned) void fetchTopRepos()
  }, [fetchTopRepos, isOrgOwned, open, ownerDisplayLogin])

  useEffect(() => {
    const fetchSearchedRepos = async (query: string) => {
      setLoading(true)

      const {repositories: fetchRepos} = await fetchSearchRepositories(relayEnvironment, {
        query: isOrgOwned
          ? getRepositoryFromOrgSearchQuery(ownerDisplayLogin, query, includeForks)
          : getRepositorySearchQuery(query, {includeForks}),
        onStart: subscription => {
          searchedReposSubscription.current?.unsubscribe()
          searchedReposSubscription.current = subscription
        },
      })

      setRepositories(fetchRepos.length ? getListItemsFromRepos(fetchRepos) : [])
      setLoading(false)
    }

    if (filterText || isOrgOwned) {
      void fetchSearchedRepos(filterText)
    } else if (topRepositoriesCache) {
      setRepositories(topRepositoriesCache)
    }
  }, [filterText, isOrgOwned, ownerDisplayLogin, relayEnvironment, topRepositoriesCache, includeForks])

  // Refresh list of top repositories based on the selected owner
  useEffect(() => {
    chatManager.setTopRepositoryTopics(undefined)
  }, [chatManager, ownerDisplayLogin])

  // If org owned, but no top repositories, just return repos from that org
  if (topRepositoriesCache && !filterText && !isOrgOwned) {
    return {repositories: topRepositoriesCache, loading: false, resetTopRepoResults}
  }

  return {repositories, loading, resetTopRepoResults}
}

function getListItemsFromRepos(repos: RepositoryPickerRepositoryLight[] | RepositoryPickerRepository[]): TopicItem[] {
  return repos.map(({owner: {login, avatarUrl}, isInOrganization, name, databaseId}) => ({
    databaseId: databaseId ?? 0,
    isInOrganization,
    name,
    nwo: `${login}/${name}`,
    ownerLogin: login,
    ownerAvatarUrl: avatarUrl,
  }))
}
